<div class="guide">
  <!-- Hero -->
  <div class="guide-hero">
    <img src="/assets/icons/xwai-icon-cropped.png" alt="XWAI" class="guide-hero-icon" />
    <div class="guide-hero-text">
      <h1>Meet XWAI</h1>
      <p>
        XWAI is your assistant for exploring jobs, parameters, lineage, velocity
        models and trace fits — in plain language. Ask a question and XWAI calls
        on a team of <b>specialists</b>, then streams back an answer alongside any
        charts, tables and diagrams it produced.
      </p>
    </div>
  </div>

  <!-- Quick nav -->
  <nav class="guide-nav">
    <a (click)="scrollTo('ask', $event)">Asking questions</a>
    <a (click)="scrollTo('anatomy', $event)">Anatomy of an answer</a>
    <a (click)="scrollTo('specialists', $event)">The specialists</a>
    <a (click)="scrollTo('tips', $event)">Tips</a>
  </nav>

  <!-- 1. Asking questions -->
  <section id="ask" class="guide-section">
    <h2><span class="guide-step">1</span> Asking questions</h2>
    <p>
      Type a question into the box at the bottom of the chat and press
      <kbd>Enter</kbd> (use <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line).
      Reference jobs by name or id — XWAI will figure out the rest. Press
      <kbd>↑</kbd> / <kbd>↓</kbd> in an empty box to recall earlier messages.
    </p>
    <p class="guide-subhead">Try one of these:</p>
    <div class="guide-prompts">
      @for (prompt of examplePrompts; track $index) {
      <span class="guide-prompt-chip">{{ prompt }}</span>
      }
    </div>
  </section>

  <!-- 2. Anatomy of an answer -->
  <section id="anatomy" class="guide-section">
    <h2><span class="guide-step">2</span> Anatomy of an answer</h2>
    <p>
      Every reply is built up live as XWAI works. A single answer can contain
      several parts — here's a labelled example:
    </p>

    <!-- Annotated mock chat message -->
    <div class="mock-chat">
      <img src="/assets/icons/xwai-icon-cropped.png" alt="" class="mock-bot-icon" />
      <div class="mock-bubble">
        <span class="callout">1</span>
        <p class="mock-answer">
          Job <b>ABC-123</b> ran 12 inversion iterations. The trace fit improved
          from 41% to 88%. Here's the comparison and the convergence chart:
        </p>

        <span class="callout callout-2">2</span>
        <div class="mock-specialist">
          <span nz-icon nzType="line-chart" nzTheme="outline"></span>
          Specialist output (table / chart / diagram) renders here
        </div>

        <div class="mock-trace">
          <span class="callout callout-3">3</span>
          <span nz-icon nzType="right" nzTheme="outline"></span> Show steps
          <em>(3 agents)</em>
        </div>

        <div class="mock-usage">
          <span class="callout callout-4">4</span>
          <span class="mock-pill"><b>Input</b> 4,210</span>
          <span class="mock-pill"><b>Output</b> 1,880</span>
          <span class="mock-pill mock-pill-cost"><b>Cost</b> 0.07</span>
        </div>
      </div>
    </div>

    <ol class="guide-legend">
      <li><b>The answer</b> — XWAI's written reply, streamed word by word.</li>
      <li><b>Specialist output</b> — rich, interactive results (tables, charts,
        model views, lineage trees) embedded right in the message.</li>
      <li><b>Show steps</b> — expand to see exactly which specialists ran, their
        reasoning and the tools they called. Great for trust and debugging.</li>
      <li><b>Usage</b> — tokens consumed and the estimated cost of the answer.</li>
    </ol>
  </section>

  <!-- 3. The specialists -->
  <section id="specialists" class="guide-section">
    <h2><span class="guide-step">3</span> The specialists</h2>
    <p>
      XWAI delegates to focused specialists and may run several in parallel for
      one question. Each returns a different kind of result:
    </p>

    <!-- Lineage -->
    <article class="spec-card">
      <div class="spec-head">
        <span class="spec-icon" nz-icon nzType="apartment" nzTheme="outline"></span>
        <div>
          <h3>Lineage specialist</h3>
          <p class="spec-tagline">Where a job came from.</p>
        </div>
      </div>
      <p>
        Traces a job's ancestry — the chain of parent jobs and checkpoints it was
        built on — and draws it as a tree so you can see how a result was reached.
      </p>
      <div class="mock mock-lineage">
        <div class="lin-row"><span class="lin-name">initial-model</span> <span class="lin-cp">cp 0</span></div>
        <div class="lin-row" style="margin-left: 18px;"><span class="lin-branch">└─</span> <span class="lin-name">tomo-run-01</span> <span class="lin-cp">cp 8</span></div>
        <div class="lin-row" style="margin-left: 36px;"><span class="lin-branch">└─</span> <span class="lin-name">fwi-run-04</span> <span class="lin-cp">cp 12</span></div>
        <div class="lin-row is-target" style="margin-left: 54px;"><span class="lin-branch">└─</span> <span class="lin-name">ABC-123</span> <span class="lin-target">★</span> <span class="lin-cp">cp 12</span></div>
      </div>
      <ul class="spec-read">
        <li>Each row is a job; indentation shows the parent → child chain.</li>
        <li><b>★</b> marks the job you asked about; <b>cp</b> is the checkpoint used.</li>
      </ul>
    </article>

    <!-- Parameters -->
    <article class="spec-card">
      <div class="spec-head">
        <span class="spec-icon" nz-icon nzType="table" nzTheme="outline"></span>
        <div>
          <h3>Parameters specialist</h3>
          <p class="spec-tagline">What a job was configured with.</p>
        </div>
      </div>
      <p>
        Pulls a job's inversion parameters into a clean table. Ask about several
        jobs and it lays them side by side so differences jump out.
      </p>
      <div class="mock mock-table">
        <div class="tbl-row tbl-head">
          <span class="tbl-cell tbl-param">Parameter</span>
          <span class="tbl-cell">fwi-run-04</span>
          <span class="tbl-cell">ABC-123</span>
        </div>
        <div class="tbl-row"><span class="tbl-cell tbl-param">Max frequency</span><span class="tbl-cell">8 Hz</span><span class="tbl-cell tbl-diff">12 Hz</span></div>
        <div class="tbl-row"><span class="tbl-cell tbl-param">Iterations</span><span class="tbl-cell">8</span><span class="tbl-cell tbl-diff">12</span></div>
        <div class="tbl-row"><span class="tbl-cell tbl-param">Step length</span><span class="tbl-cell">0.02</span><span class="tbl-cell">0.02</span></div>
      </div>
      <ul class="spec-read">
        <li>Parameters run down the side, one column per job.</li>
        <li>Scan across a row to compare a setting between jobs.</li>
      </ul>
    </article>

    <!-- Visualization -->
    <article class="spec-card">
      <div class="spec-head">
        <span class="spec-icon" nz-icon nzType="picture" nzTheme="outline"></span>
        <div>
          <h3>Visualization specialist</h3>
          <p class="spec-tagline">See the velocity / property model.</p>
        </div>
      </div>
      <p>
        Renders a slice of a job's model. It's interactive: switch model type and
        colormap, and scrub through inversion iterations to watch the model evolve.
      </p>
      <div class="mock mock-viz">
        <div class="viz-card-mock">
          <div class="viz-head-mock">
            <b>ABC-123</b>
            <span class="viz-badge-mock">Model type ▾ <span class="callout callout-inline">1</span></span>
          </div>
          <div class="viz-canvas-mock">
            <div class="viz-gradient"></div>
            <span class="viz-canvas-label">model slice</span>
          </div>
          <div class="viz-meta-mock">
            <span class="mock-pill">Colormap ▾ <span class="callout callout-inline">2</span></span>
            <span class="mock-pill viz-iter">Iterations <span class="viz-slider"></span> <span class="callout callout-inline">3</span></span>
          </div>
        </div>
      </div>
      <ol class="guide-legend guide-legend-tight">
        <li><b>Model type</b> — Vp, density, and other available properties.</li>
        <li><b>Colormap</b> — recolour the slice to taste.</li>
        <li><b>Iterations</b> — drag to step through the inversion.</li>
      </ol>
    </article>

    <!-- Trace-fit -->
    <article class="spec-card">
      <div class="spec-head">
        <span class="spec-icon" nz-icon nzType="dot-chart" nzTheme="outline"></span>
        <div>
          <h3>Trace-fit specialist</h3>
          <p class="spec-tagline">How well the model matches the data.</p>
        </div>
      </div>
      <p>
        Plots a trace-fit metric — how closely modelled data matches the observed
        seismic traces. View it two ways: <b>over iterations</b> (convergence) or
        <b>spatially</b> across the survey, aggregated into distance windows.
      </p>
      <div class="mock mock-tracefit">
        <div class="viz-card-mock">
          <div class="viz-head-mock">
            <b>Trace-fit analysis</b>
            <span class="viz-badge-mock">2 jobs</span>
          </div>
          <div class="viz-canvas-mock">
            <svg viewBox="0 0 260 90" class="tf-spark" preserveAspectRatio="none">
              <polyline points="0,70 50,55 100,40 150,28 200,18 260,12" />
              <polyline class="tf-line-2" points="0,78 50,66 100,52 150,44 200,38 260,30" />
            </svg>
          </div>
          <div class="viz-meta-mock viz-meta-stack">
            <span class="mock-sec-title">Data</span>
            <div class="mock-row">
              <span class="mock-pill">View <b>Iteration | Spatial</b> <span class="callout callout-inline">1</span></span>
              <span class="mock-pill">Profile ▾ <span class="callout callout-inline">2</span></span>
            </div>
            <span class="mock-sec-title">Display</span>
            <div class="mock-row">
              <span class="mock-pill">Type <b>Line | Scatter</b></span>
              <span class="mock-pill">Y range <span class="callout callout-inline">3</span></span>
            </div>
          </div>
        </div>
      </div>
      <ol class="guide-legend guide-legend-tight">
        <li><b>View</b> — <i>Iteration</i> shows convergence over time;
          <i>Spatial</i> plots the fit along inline / crossline.</li>
        <li><b>Profile</b> — which fit metric to chart.</li>
        <li><b>Y / X range</b> — clamp the axes; leave blank to auto-scale.</li>
      </ol>
      <p class="spec-note">
        In <b>Spatial</b> view a <b>Window (m)</b> control averages nearby shots
        into fixed-width bins, so dense, irregular shots read as a clean line.
        Hover the <span nz-icon nzType="question-circle" nzTheme="outline"></span>
        on any chart for the same explanation in context.
      </p>
    </article>
  </section>

  <!-- 4. Tips -->
  <section id="tips" class="guide-section">
    <h2><span class="guide-step">4</span> Tips &amp; good to know</h2>
    <ul class="guide-tips">
      <li><b>Be specific.</b> Naming a job id or a metric gets you a sharper answer.</li>
      <li><b>Follow up.</b> XWAI keeps the conversation's context, so "and the other job?" works.</li>
      <li><b>It works in parallel.</b> One question can fan out to several
        specialists at once — that's why a reply may show more than one chart or table.</li>
      <li><b>Open "Show steps"</b> if an answer surprises you — you'll see exactly
        what XWAI looked at.</li>
      <li><b>Watch the cost pill</b> to keep an eye on token usage per answer.</li>
    </ul>
  </section>

  <p class="guide-footer">That's the tour — close this panel and ask XWAI anything.</p>
</div>
